/**
 * An instrument's coin mark. 2026-08-05 revamp.
 *
 * ## 🔴 73 of the 74 traded instruments have a real mark, and the one that does not must not
 * look broken
 *
 * **The source is now OKX's own currency CDN, not `cryptologos.cc`** (2026-08-06). Coverage went
 * 43 → 73 in the same move, and the reason it was 43 is that `cryptologos.cc` does not carry the
 * newer listings — HYPE, PENGU, PUMP, MON, KAT, MEGA, IRYS, XPL and sixteen more, which is
 * precisely the small-cap end D24 targets.
 *
 * ⚠ **Coverage is the visible reason and identity is the better one.** A ticker is ambiguous and
 * a general icon set resolves it by guessing: `BABY` in this universe is **Babylon**, and
 * `Cryptofonts/cryptoicons` — the best-covered alternative at 55 — ships a `babydoge.svg` that a
 * ticker match picks up happily. `NES` is not the `nest.svg` it also ships. **A wrong mark is
 * worse than none**, because it misidentifies a holding on a screen whose job is to say what is
 * held. The venue cannot be wrong about what a symbol means here: the universe *is* its listing.
 *
 * Choosing the venue also avoided taking a GPL-3.0 dependency for decoration.
 *
 * Still **self-hosted, never hot-linked**, and only the first reason is about looks: a remote
 * image on a trading screen is a third-party host that can go down, change a file under us, or
 * **watch which instruments this operator holds** — on every render, as a side effect of drawing
 * an icon. `ops/fetch_coin_logos.py` fetches them once; `assets/coins/PROVENANCE.md` records
 * where they came from and what removing them costs.
 *
 * ⚠ **So the fallback is the design, not the exception.** A screen showing real logos for two
 * thirds of its rows and a broken-image glyph for the rest is worse than one with no logos at
 * all, because the missing third reads as a fault in the app rather than a gap in a logo CDN.
 * The fallback is a monogram tile: the ticker's first two characters in the product's own type,
 * on a surface derived from the ticker itself, so it is stable per instrument and looks
 * deliberate beside a real mark.
 *
 * The hue is `--data-ink`-neutral by default and *not* a generated colour wheel. 🔴 **A
 * per-instrument colour would break the colour law**: green, red and amber mean profit, loss
 * and real money in this product, and a monogram that happened to come out green would be
 * asserting a gain. The tile is neutral, and identity comes from the letters.
 *
 * ## Licensing, stated rather than assumed
 *
 * These are third-party trademarks and OKX grants nothing with them, so what makes this use
 * fine is that it is **nominative**: the mark
 * identifies the coin the row is actually about, in a single-operator internal tool, with no
 * suggestion of endorsement. That is the normal basis every exchange UI uses. It would stop
 * being fine the moment this became a public marketing surface, so the note travels with the
 * component rather than living in a commit message.
 */

import { type JSX, useState } from 'react';

import coinUrls from './coinUrls';


/** `BTC/EUR` → `BTC`. A symbol is always base/quote here; the base is what carries a logo. */
export function baseOf(symbol: string): string {
  return (symbol.split('/')[0] ?? symbol).toUpperCase();
}

export function CoinMark({
  symbol,
  size = 24,
}: {
  symbol: string;
  size?: number;
}): JSX.Element {
  const base = baseOf(symbol);
  const [failed, setFailed] = useState(false);
  // The map is built by globbing the asset directory, so a coin with a file always has a mark
  // and one without always falls back — there is no third state where the two disagree.
  const url = coinUrls[base.toLowerCase()];
  const real = url !== undefined && !failed;

  return (
    <span
      className="jmt-coin"
      data-fallback={real ? undefined : ''}
      style={{ width: size, height: size }}
      // The accessible name is the ticker, not "logo": the row already says BTC/EUR, so an
      // image announcing "Bitcoin logo" is a second reading of the same fact.
      aria-hidden="true"
      title={base}
    >
      {real ? (
        <img
          src={url}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="jmt-coin__mono">{base.slice(0, 2)}</span>
      )}
    </span>
  );
}
